'use client';

import ShareCard from '@/app/(public)/share/share-card';
import { SquarePost } from '@/app/actions/share';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export function ShareCarousel({ list }: { list: SquarePost[] }) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const dragState = useRef({
    hasDragged: false,
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
  });

  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const newCanScrollLeft = container.scrollLeft > 0;
    const newCanScrollRight =
      container.scrollLeft < container.scrollWidth - container.clientWidth - 10;

    setCanScrollLeft((prev) =>
      prev !== newCanScrollLeft ? newCanScrollLeft : prev,
    );
    setCanScrollRight((prev) =>
      prev !== newCanScrollRight ? newCanScrollRight : prev,
    );
  }, []);

  useEffect(() => {
    checkScrollability();

    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkScrollability, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [checkScrollability]);

  const handleScroll = useCallback(() => {
    checkScrollability();
  }, [checkScrollability]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.8;
    const targetScroll =
      direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    dragState.current = {
      hasDragged: false,
      isDragging: true,
      startX: e.pageX - container.offsetLeft,
      scrollLeft: container.scrollLeft,
    };
    container.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragState.current.isDragging) return;
    e.preventDefault();

    const container = scrollContainerRef.current;
    if (!container) return;

    const x = e.pageX - container.offsetLeft;
    const walk = (x - dragState.current.startX) * 2;
    if (Math.abs(walk) > 6) {
      dragState.current.hasDragged = true;
    }
    container.scrollLeft = dragState.current.scrollLeft - walk;
  };

  const handleMouseUp = () => {
    dragState.current.isDragging = false;
    const container = scrollContainerRef.current;
    if (container) {
      container.style.cursor = 'grab';
    }
  };

  const handleClickCapture = (e: React.MouseEvent) => {
    if (!dragState.current.hasDragged) return;

    e.preventDefault();
    e.stopPropagation();
    dragState.current.hasDragged = false;
  };

  const handleMouseLeave = () => {
    if (dragState.current.isDragging) {
      dragState.current.isDragging = false;
      const container = scrollContainerRef.current;
      if (container) {
        container.style.cursor = 'grab';
      }
    }
  };

  return (
    <section>
      <div className="relative group/carousel">
        <Button
          variant="outline"
          size="icon"
          className={cn(
            'absolute -left-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full',
            'bg-background border border-border hover:border-foreground/20',
            'transition-all duration-200',
            canScrollLeft
              ? 'opacity-0 group-hover/carousel:opacity-100'
              : 'opacity-0 pointer-events-none',
          )}
          onClick={() => scroll('left')}
          aria-label="向左滑动"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onClickCapture={handleClickCapture}
          className="flex gap-3 overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing select-none py-1 -my-1"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {list.map((item, index) => (
            <ShareCard
              key={item.post_id}
              item={item}
              className="md:w-[22%] w-[85%] shrink-0"
              priority={index < 4}
            />
          ))}
        </div>

        <Button
          variant="outline"
          size="icon"
          className={cn(
            'absolute -right-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full',
            'bg-background border border-border hover:border-foreground/20',
            'transition-all duration-200',
            canScrollRight
              ? 'opacity-0 group-hover/carousel:opacity-100'
              : 'opacity-0 pointer-events-none',
          )}
          onClick={() => scroll('right')}
          aria-label="向右滑动"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>
    </section>
  );
}
